import config from "../config";
import selectedEnvironment from "../env";

const API_URL = `${config[selectedEnvironment].API_URL}`;

const handleResponse = async (response, errorMessage) => {
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
    }
    return response.json();
};

const GetAllAssets = async (caseId, authToken, year = null) => {
    try {
        const url = year != null
            ? `${API_URL}/Assets/GetAssetsByCaseId/${encodeURIComponent(caseId)}?year=${year}`
            : `${API_URL}/Assets/GetAssetsByCaseId/${encodeURIComponent(caseId)}`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        return await handleResponse(response, "Failed to fetch assets");
    } catch (error) {
        console.error("Error in GetAllAssets:", error);
        throw error;
    }
};

const GetAssetById = async (caseId, assetId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/GetAssetById`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId: caseId, AssetId: assetId }),
        });

        return await handleResponse(response, "Failed to fetch asset by ID");
    } catch (error) {
        console.error("Error in GetAssetById:", error);
        throw error;
    }
};

const GetAssetByNumber = async (caseId, assetNumber, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/GetAssetsByNumber`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId: caseId, AssetNumber: assetNumber }),
        });

        return await handleResponse(response, "Failed to fetch asset by number");
    } catch (error) {
        console.error("Error in GetAssetByNumber:", error);
        throw error;
    }
};

const CreateAsset = async (formData, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/CreateAsset`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        });

        return await handleResponse(response, "Failed to create asset");
    } catch (error) {
        console.error("Error in CreateAsset:", error);
        throw error;
    }
};

const UpdateAsset = async (formData, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/UpdateAsset`, {
            method: "PUT",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        });

        return await handleResponse(response, "Failed to update asset");
    } catch (error) {
        console.error("Error in UpdateAsset:", error);
        throw error;
    }
};

const CanDeleteAsset = async (caseId, assetId, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CanDeleteAsset/${encodeURIComponent(caseId)}/${encodeURIComponent(assetId)}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        return await handleResponse(response, 'Failed to check if asset can be deleted');
    } catch (error) {
        console.error('Error in CanDeleteAsset:', error);
        throw error;
    }
};

const DeleteAsset = async (caseId, assetId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/DeleteAsset`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId: caseId, AssetId: assetId }),
        });

        return await handleResponse(response, "Failed to delete asset");
    } catch (error) {
        console.error("Error in DeleteAsset:", error);
        throw error;
    }
};

// ---------------------------
//          ASSET GROUPS
// ---------------------------

const GetAllAssetGroups = async (caseId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/GetAssetGroupsByCaseId/${encodeURIComponent(caseId)}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        return await handleResponse(response, "Failed to fetch asset groups");
    } catch (error) {
        console.error("Error in GetAllAssetGroups:", error);
        throw error;
    }
};

const GetAssetGroupById = async (caseId, assetGroupId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/GetAssetGroupById`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId: caseId, AssetGroupId: assetGroupId }),
        });

        return await handleResponse(response, "Failed to fetch asset group by ID");
    } catch (error) {
        console.error("Error in GetAssetGroupById:", error);
        throw error;
    }
};

const CreateAssetGroup = async (formData, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/CreateAssetGroup`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        });

        return await handleResponse(response, "Failed to create asset group");
    } catch (error) {
        console.error("Error in CreateAssetGroup:", error);
        throw error;
    }
};

const UpdateAssetGroup = async (formData, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/UpdateAssetGroup`, {
            method: "PUT",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        });

        return await handleResponse(response, "Failed to update asset group");
    } catch (error) {
        console.error("Error in UpdateAssetGroup:", error);
        throw error;
    }
};

const DeleteAssetGroup = async (caseId, assetGroupId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/DeleteAssetGroup`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ CaseId: caseId, AssetGroupId: assetGroupId }),
        });

        return await handleResponse(response, "Failed to delete asset group");
    } catch (error) {
        console.error("Error in DeleteAssetGroup:", error);
        throw error;
    }
};

const GetDepreciationDetails = async (caseId, date, authToken, endDate = null) => {
    try {
        const body = { CaseId: caseId };
        if (date) body.Date = new Date(date).toISOString().split("T")[0];
        if (endDate) body.EndDate = new Date(endDate).toISOString().split("T")[0];

        const response = await fetch(`${API_URL}/Assets/DepreciationDetails`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        });

        return await handleResponse(response, "Failed to fetch depreciation details");
    } catch (error) {
        console.error("Error in GetDepreciationDetails:", error);
        throw error;
    }
};

const AddDocumentToAsset = async (caseId, assetId, type, documentId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/AddDocumentToAsset`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                CaseId: caseId,
                AssetId: assetId,
                Type: type,  // "Expense", "Income", or "JournalEntry"
                DocumentId: documentId
            }),
        });

        return await handleResponse(response, "Failed to add document to asset");
    } catch (error) {
        console.error("Error in AddDocumentToAsset:", error);
        throw error;
    }
};

const SetEndDate = async (caseId, assetId, type, documentId, sellDate, authToken, sellAmount = null) => {
    try {
      const body = {
        CaseId: caseId,
        AssetId: assetId,
        SellDate: sellDate,
        Type: type,
        TransactionId: documentId,
      };
      if (sellAmount != null) body.SellAmount = sellAmount;

      const response = await fetch(`${API_URL}/Assets/SetEndDate`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
  
      return await handleResponse(response, "Failed to set end date for asset");
    } catch (error) {
      console.error("Error in SetEndDate:", error);
      throw error;
    }
  };

const GetJournalEntriesAndExpensesWithVatEquipment = async (caseId, accountantId, date, authToken) => {
    try {
        const body = { CaseId: caseId, AccountantId: accountantId };
        if (date != null) body.Date = typeof date === 'string' ? date : new Date(date).toISOString();

        const response = await fetch(`${API_URL}/Assets/GetJournalEntriesAndExpensesWithVatEquipment`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        });

        return await handleResponse(response, "Failed to fetch journal entries and expenses with VatEquipment");
    } catch (error) {
        console.error("Error in GetJournalEntriesAndExpensesWithVatEquipment:", error);
        throw error;
    }
};

const GetPurchaseOfEquipmentBalance = async (caseId, accountantId, authToken, date = null) => {
    try {
        const body = { CaseId: caseId, AccountantId: accountantId };
        if (date != null) body.Date = typeof date === 'string' ? date : new Date(date).toISOString();

        const response = await fetch(`${API_URL}/Assets/GetPurchaseOfEquipmentBalance`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        });

        return await handleResponse(response, "Failed to fetch equipment balance");
    } catch (error) {
        console.error("Error in GetPurchaseOfEquipmentBalance:", error);
        throw error;
    }
};

const DetachDocumentFromAsset = async (caseId, assetId, transactionId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/DetachDocumentFromAsset`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                CaseId: caseId,
                AssetId: assetId,
                TransactionId: transactionId,
            }),
        });

        return await handleResponse(response, "Failed to detach document from asset");
    } catch (error) {
        console.error("Error in DetachDocumentFromAsset:", error);
        throw error;
    }
};



const PreviewAssetImport = async (caseId, file, authToken) => {
    try {
        const form = new FormData();
        form.append('CaseId', caseId);
        form.append('File', file);

        const response = await fetch(`${API_URL}/Assets/preview-asset-import`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}` },
            body: form,
        });

        return await handleResponse(response, 'Failed to preview asset import');
    } catch (error) {
        console.error('Error in PreviewAssetImport:', error);
        throw error;
    }
};

const ImportAssets = async (caseId, file, authToken, year = null) => {
    try {
        const form = new FormData();
        form.append('CaseId', caseId);
        form.append('File', file);
        if (year != null) form.append('Year', year.toString());

        const response = await fetch(`${API_URL}/Assets/import-assets`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}` },
            body: form,
        });

        return await handleResponse(response, 'Failed to import assets');
    } catch (error) {
        console.error('Error in ImportAssets:', error);
        throw error;
    }
};

const SaveDepreciationSnapshot = async (caseId, year, items, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/SaveDepreciationSnapshot`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                CaseId: caseId,
                Year: year,
                Items: items,
            }),
        });
        return await handleResponse(response, 'Failed to save depreciation snapshot');
    } catch (error) {
        console.error('Error in SaveDepreciationSnapshot:', error);
        throw error;
    }
};

const GetDepreciationSnapshots = async (caseId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/GetDepreciationSnapshots/${encodeURIComponent(caseId)}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        });
        return await handleResponse(response, 'Failed to fetch depreciation snapshots');
    } catch (error) {
        console.error('Error in GetDepreciationSnapshots:', error);
        throw error;
    }
};

const DeleteDepreciationSnapshot = async (caseId, year, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/DeleteDepreciationSnapshot/${encodeURIComponent(caseId)}/${year}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        });
        return await handleResponse(response, 'Failed to delete depreciation snapshot');
    } catch (error) {
        console.error('Error in DeleteDepreciationSnapshot:', error);
        throw error;
    }
};

// ---------------------------
//        ASSET RELATIONS
// ---------------------------

const GetAssetRelationsByCaseId = async (caseId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/GetAssetRelationsByCaseId/${encodeURIComponent(caseId)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
        });
        return await handleResponse(response, 'Failed to fetch asset relations');
    } catch (error) {
        console.error('Error in GetAssetRelationsByCaseId:', error);
        throw error;
    }
};

const GetAssetRelationByRootAsset = async (caseId, rootAssetId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/GetAssetRelationByRootAsset/${encodeURIComponent(caseId)}/${encodeURIComponent(rootAssetId)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
        });
        return await handleResponse(response, 'Failed to fetch asset relation');
    } catch (error) {
        console.error('Error in GetAssetRelationByRootAsset:', error);
        throw error;
    }
};

const SaveAssetRelation = async ({ caseId, rootAssetId, relatedAssetIds }, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/SaveAssetRelation`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                CaseId: caseId,
                RootAssetId: rootAssetId,
                RelatedAssetIds: relatedAssetIds || [],
            }),
        });
        return await handleResponse(response, 'Failed to save asset relation');
    } catch (error) {
        console.error('Error in SaveAssetRelation:', error);
        throw error;
    }
};

const GetCapitalGainIndexCalculation = async (purchaseDate, saleDate, authToken) => {
    try {
        const purchase = new Date(purchaseDate).toISOString();
        const sale = new Date(saleDate).toISOString();
        const url =
            `${API_URL}/Assets/CapitalGainIndexCalculation` +
            `?purchaseDate=${encodeURIComponent(purchase)}` +
            `&saleDate=${encodeURIComponent(sale)}`;
        console.log('url', url);    
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
        });
        return await handleResponse(response, 'Failed to calculate capital gain index');
    } catch (error) {
        console.error('Error in GetCapitalGainIndexCalculation:', error);
        throw error;
    }
};

const GetCapitalGainReplacements = async (caseId, soldAssetId, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CapitalGainReplacements/${encodeURIComponent(caseId)}/${encodeURIComponent(soldAssetId)}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return await handleResponse(response, 'Failed to fetch capital gain replacements');
    } catch (error) {
        console.error('Error in GetCapitalGainReplacements:', error);
        throw error;
    }
};

const SaveCapitalGainReplacements = async (caseId, payload, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CapitalGainReplacements/${encodeURIComponent(caseId)}`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }
        );
        return await handleResponse(response, 'Failed to save capital gain replacements');
    } catch (error) {
        console.error('Error in SaveCapitalGainReplacements:', error);
        throw error;
    }
};

const DeleteCapitalGainReplacement = async (caseId, replacementId, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CapitalGainReplacements/${encodeURIComponent(caseId)}/${encodeURIComponent(replacementId)}`,
            {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return await handleResponse(response, 'Failed to delete capital gain replacement');
    } catch (error) {
        console.error('Error in DeleteCapitalGainReplacement:', error);
        throw error;
    }
};

const GetCapitalGainReplacementCandidates = async (caseId, soldAssetId, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CapitalGainReplacementCandidates/${encodeURIComponent(caseId)}/${encodeURIComponent(soldAssetId)}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return await handleResponse(response, 'Failed to fetch replacement candidates');
    } catch (error) {
        console.error('Error in GetCapitalGainReplacementCandidates:', error);
        throw error;
    }
};

const GetCapitalGainEnhancements = async (caseId, soldAssetId, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CapitalGainEnhancements/${encodeURIComponent(caseId)}/${encodeURIComponent(soldAssetId)}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return await handleResponse(response, 'Failed to fetch capital gain enhancements');
    } catch (error) {
        console.error('Error in GetCapitalGainEnhancements:', error);
        throw error;
    }
};

const SaveCapitalGainEnhancements = async (caseId, payload, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CapitalGainEnhancements/${encodeURIComponent(caseId)}`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }
        );
        return await handleResponse(response, 'Failed to save capital gain enhancements');
    } catch (error) {
        console.error('Error in SaveCapitalGainEnhancements:', error);
        throw error;
    }
};

const GetEnhancementCandidates = async (caseId, rootAssetId, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/EnhancementCandidates/${encodeURIComponent(caseId)}/${encodeURIComponent(rootAssetId)}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return await handleResponse(response, 'Failed to fetch enhancement candidates');
    } catch (error) {
        console.error('Error in GetEnhancementCandidates:', error);
        throw error;
    }
};

const GetCapitalGainDepreciationAtSale = async (caseId, assetId, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/CapitalGainDepreciationAtSale/${encodeURIComponent(caseId)}/${encodeURIComponent(assetId)}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return await handleResponse(response, 'Failed to fetch capital gain basis');
    } catch (error) {
        console.error('Error in GetCapitalGainDepreciationAtSale:', error);
        throw error;
    }
};

const DownloadCapitalGainPdf = async (caseId, soldAssetId, payload, authToken) => {
    try {
        const response = await fetch(
            `${API_URL}/Assets/DownloadCapitalGainPdf/${encodeURIComponent(caseId)}/${encodeURIComponent(soldAssetId)}`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }
        );

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Failed to download capital gain PDF');
        }

        return await response.blob();
    } catch (error) {
        console.error('Error in DownloadCapitalGainPdf:', error);
        throw error;
    }
};

const DeleteAssetRelation = async (caseId, rootAssetId, authToken) => {
    try {
        const response = await fetch(`${API_URL}/Assets/DeleteAssetRelation/${encodeURIComponent(caseId)}/${encodeURIComponent(rootAssetId)}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
        });
        return await handleResponse(response, 'Failed to delete asset relation');
    } catch (error) {
        console.error('Error in DeleteAssetRelation:', error);
        throw error;
    }
};

// Export AssetService object
const AssetService = {
    GetAllAssets,
    GetAssetById,
    GetAssetByNumber,
    CreateAsset,
    UpdateAsset,
    CanDeleteAsset,
    DeleteAsset,
    GetAllAssetGroups,
    GetAssetGroupById,
    CreateAssetGroup,
    UpdateAssetGroup,
    DeleteAssetGroup,
    GetDepreciationDetails,
    AddDocumentToAsset,
    SetEndDate,
    GetJournalEntriesAndExpensesWithVatEquipment,
    GetPurchaseOfEquipmentBalance,
    DetachDocumentFromAsset,
    PreviewAssetImport,
    ImportAssets,
    SaveDepreciationSnapshot,
    GetDepreciationSnapshots,
    DeleteDepreciationSnapshot,
    GetAssetRelationsByCaseId,
    GetAssetRelationByRootAsset,
    SaveAssetRelation,
    DeleteAssetRelation,
    GetCapitalGainIndexCalculation,
    GetCapitalGainDepreciationAtSale,
    GetCapitalGainReplacements,
    SaveCapitalGainReplacements,
    DeleteCapitalGainReplacement,
    GetCapitalGainReplacementCandidates,
    GetCapitalGainEnhancements,
    SaveCapitalGainEnhancements,
    GetEnhancementCandidates,
    DownloadCapitalGainPdf,
};

export default AssetService;
